import { useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { StudentQuestion } from '@/api/ieltsMock';
import { LayoutBody } from '@/components/ielts/LayoutBody';
import { bankPhrases, gapOrder, nextEmptyGap, phraseLetter } from '@/lib/bankBox';
import { questionNumber } from '@/lib/mockReview';
import type { ServerLayout } from '@/lib/serverLayout';

/**
 * Банк фраз как на экзамене (T3, 29.10.2026; логика — `lib/bankBox.ts`): абзац с пропусками, под ним
 * рамка фраз A–J. Касание: выбранный пропуск подсвечен, фраза встаёт в него, выбор переходит к
 * следующему пустому. На компьютере фразу можно перетащить в пропуск. На телефоне перетаскивания нет
 * (браузер его не даёт пальцем) — только касание, поэтому оно и главное.
 *
 * Значение ответа — текст фразы (как у выпадающего списка прежней карточки).
 */
type Props = {
  layout: ServerLayout;
  options: string[] | undefined;
  answers: Record<string, string>;
  onAnswer: (qid: string, val: string) => void;
  num: (q: StudentQuestion) => ReactNode;
};

const DRAG_TYPE = 'text/plain';

export function BankBody({ layout, options, answers, onAnswer, num }: Props) {
  const { t } = useTranslation();
  const phrases = bankPhrases(options);
  const order = gapOrder(layout);
  const [active, setActive] = useState<string | null>(() => nextEmptyGap(order, answers, null) ?? order[0]?.id ?? null);
  const [over, setOver] = useState<string | null>(null);
  const used = new Set(order.map((q) => answers[q.id] ?? '').filter((v) => v !== ''));

  const place = (qid: string, text: string) => {
    onAnswer(qid, text);
    setActive(nextEmptyGap(order, { ...answers, [qid]: text }, qid));
  };

  const drop = (qid: string) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const text = e.dataTransfer.getData(DRAG_TYPE);
    if (phrases.some((p) => p.text === text)) place(qid, text);
  };

  const gap = (q: StudentQuestion) => {
    const value = answers[q.id] ?? '';
    const letter = phraseLetter(phrases, value);
    const n = questionNumber(q.ord, q.ord_to);
    const lit = active === q.id || over === q.id;
    return (
      <span className="mx-1 inline-flex max-w-full items-center gap-1 align-middle">
        {num(q)}
        <button
          type="button"
          aria-pressed={active === q.id}
          aria-label={t('ieltsMock.bankGap', { n })}
          onClick={() => setActive(q.id)}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(q.id);
          }}
          onDragLeave={() => setOver(null)}
          onDrop={drop(q.id)}
          className={`inline-flex min-h-8 min-w-[7rem] max-w-full items-center rounded border px-2 py-0.5 text-left text-sm leading-snug ${
            value ? 'border-outline bg-surface' : 'border-dashed border-on-surface-variant'
          } ${lit ? 'border-primary ring-2 ring-primary/40' : ''}`}
        >
          {value && (
            <span className="min-w-0 whitespace-normal">
              {letter && <b className="mr-1">{letter}</b>}
              {value}
            </span>
          )}
        </button>
        {value && (
          <button
            type="button"
            aria-label={t('ieltsMock.bankClear', { n })}
            onClick={() => {
              onAnswer(q.id, '');
              setActive(q.id);
            }}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-on-surface-variant hover:bg-on-surface/10"
          >
            <span aria-hidden>×</span>
          </button>
        )}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <LayoutBody layout={layout} gap={gap} />
      <p className="text-xs text-on-surface-variant">{t('ieltsMock.bankHint')}</p>
      <ul
        aria-label={t('ieltsMock.bankBox')}
        className="grid grid-cols-1 gap-2 rounded-lg border border-outline bg-surface/60 p-3 sm:grid-cols-2"
      >
        {phrases.map((p) => (
          <li key={p.letter}>
            <button
              type="button"
              draggable
              // Не disabled: у выключенной кнопки браузер не начинает перетаскивание, а когда все пропуски
              // заполнены (выбранного нет), фразу всё равно можно перетащить на замену.
              aria-disabled={active === null}
              onDragStart={(e) => {
                e.dataTransfer.setData(DRAG_TYPE, p.text);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => active !== null && place(active, p.text)}
              className={`flex w-full items-start gap-2 rounded border border-outline bg-surface px-2 py-1.5 text-left text-sm text-on-surface hover:border-primary ${
                used.has(p.text) ? 'opacity-50' : ''
              }`}
            >
              <b className="w-4 shrink-0">{p.letter}</b>
              <span className="min-w-0">{p.text}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
